import { html, unsafeCSS, css } from 'lit';
import { BaseControl } from './Base';
import { property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class RDTextBox extends BaseControl {
  @property({ type: String }) value = '';
  @property({ type: String }) placeholder = '';
  @property({ type: Boolean }) readonly = false;
  @property({ type: Function }) onChange: (value: string) => void = () => {};
  @property({ type: Function }) onInput: (value: string) => void = () => {};
  @property({ type: String, reflect: true }) type = 'text';
  @property({ type: Number, reflect: true }) min: number | null = -Infinity;
  @property({ type: Number, reflect: true }) max: number | null = Infinity;
  @property({ type: Boolean, reflect: true }) hideArrows = true;

  _onChange(e: Event) {
    const input = e.target as HTMLInputElement;
    let newValue = input.value;
    if (this.type === 'number' && typeof this.min === 'number' && typeof this.max === 'number') {
      newValue = this._clampNumber(newValue);
    }
    this.value = newValue;
    this.onChange?.(newValue);
  }

  _onInput(e: Event) {
    const input = e.target as HTMLInputElement;

    let newValue = input.value;
    this.value = newValue;
    if (this.type === 'number') {
      newValue = this._clampNumber(newValue);
    }

    this.onInput?.(newValue);
  }

  _clampNumber(rawValue: string): string {
    // allow clearing the field
    if (rawValue === '') return rawValue;

    // allow just "-" or "+" while typing
    if (rawValue === '-' || rawValue === '+') return rawValue;

    const num = Number(rawValue);
    if (!Number.isFinite(num)) return rawValue;

    const min = typeof this.min === 'number' ? this.min : -Infinity;
    const max = typeof this.max === 'number' ? this.max : Infinity;

    const clamped = Math.min(max, Math.max(min, num));
    return String(clamped);
  }

  updated(changedProperties) {
    super.updated(changedProperties);
    // Sync input value when value property changes externally
    if (changedProperties.has('value')) {
      const input = this.querySelector('.rd-text-box') as HTMLInputElement;
      if (input && input.value !== this.value) {
        input.value = this.value;
      }
    }
  }

  protected renderBody() {
    return html`<input type=${this.type || 'text'} class="rd-text-box" ?hideArrows=${this.hideArrows} .value=${this.value || ''} placeholder=${this.placeholder} ?readonly=${this.readonly} @change=${this._onChange} @input=${this._onInput} .min=${this.min} .max=${this.max} />`;
  }
}
if (!customElements.get('rd-text-box')) {
  customElements.define('rd-text-box', RDTextBox);
}
